import React, { useState } from 'react';
import { CannCompoundingSurveyData } from '../../../types/survey.types';
import { useFirebase } from '../../../hooks/useFirebase';
import CannCompoundingSurvey from '../CannCompoundingSurvey';

interface SurveySubmissionHandlerProps {
  onSuccess?: (responseId: string) => void;
  onError?: (error: string) => void;
}

const SurveySubmissionHandler: React.FC<SurveySubmissionHandlerProps> = ({
  onSuccess,
  onError
}) => {
  const { submitSurvey, loading, error } = useFirebase();
  const [submissionState, setSubmissionState] = useState<{
    isSubmitted: boolean;
    responseId?: string;
    error?: string;
  }>({
    isSubmitted: false
  });

  const handleSurveySubmit = async (surveyData: CannCompoundingSurveyData) => {
    try {
      setSubmissionState({ isSubmitted: false });

      // Submit the survey
      const result = await submitSurvey(surveyData);

      if (result.success && result.data) {
        // Clear the draft from localStorage on successful submission
        localStorage.removeItem('cannCompoundingSurveyDraft');

        setSubmissionState({
          isSubmitted: true,
          responseId: result.data
        });

        // Call success callback if provided
        onSuccess?.(result.data);
      } else {
        const errorMessage = result.error || 'An unexpected error occurred during submission';
        setSubmissionState({
          isSubmitted: false,
          error: errorMessage
        });

        // Call error callback if provided
        onError?.(errorMessage);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit survey';
      setSubmissionState({
        isSubmitted: false,
        error: errorMessage
      });

      onError?.(errorMessage);
    }
  };

  const handleStartNewSurvey = () => {
    setSubmissionState({ isSubmitted: false });
    localStorage.removeItem('cannCompoundingSurveyDraft');
  };

  // Show success message if survey was submitted successfully
  if (submissionState.isSubmitted) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Thank You for Your Feedback!
          </h2>

          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Your survey has been successfully submitted. Your feedback is valuable in helping us
            improve our compounded cannabis products and services. We appreciate the time you
            took to share your experience with us.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 max-w-2xl mx-auto">
            <p className="text-sm text-blue-900 font-medium">
              <strong>Reference ID:</strong> {submissionState.responseId}
            </p>
            <p className="text-sm text-blue-900 mt-1">
              Please keep this reference number for your records.
            </p>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleStartNewSurvey}
              className="px-6 py-3 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            >
              Submit Another Survey
            </button>

            <div className="text-sm text-gray-500">
              <p>
                If you experience any urgent adverse effects, please contact your healthcare
                provider immediately or call emergency services.
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">What happens next?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
              <div>
                <div className="font-medium text-gray-900 mb-1">Review Process</div>
                <p>Our quality team will review your feedback within 1-2 business days.</p>
              </div>
              <div>
                <div className="font-medium text-gray-900 mb-1">Follow-up</div>
                <p>If needed, we may contact your healthcare provider for clarification.</p>
              </div>
              <div>
                <div className="font-medium text-gray-900 mb-1">Improvements</div>
                <p>Your feedback helps us continuously improve our products and services.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error message if there was a submission error
  if (submissionState.error || error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 mb-2">Submission Error</h3>
              <p className="text-sm text-red-700">
                {submissionState.error || error || 'An unexpected error occurred while submitting your survey.'}
              </p>
              <div className="mt-4">
                <button
                  onClick={() => setSubmissionState({ isSubmitted: false })}
                  className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Still show the survey form so user can try again */}
        <CannCompoundingSurvey
          onSubmit={handleSurveySubmit}
          loading={loading}
        />
      </div>
    );
  }

  // Show the survey form
  return (
    <CannCompoundingSurvey
      onSubmit={handleSurveySubmit}
      loading={loading}
    />
  );
};

export default SurveySubmissionHandler;